import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { marked } from "marked";
import { api, fmtDate } from "../lib/api";
import { ARTICLE_DISCLAIMER_I18N as ARTICLE_DISCLAIMER, UI } from "../data/content";
import { Reveal, ArticleCard, SubscribeForm, ArrowLink, usePageMeta } from "../components/kit";

export default function Article() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setA(null); setNotFound(false);
    api.get(`/articles/${slug}`).then(({ data }) => setA(data)).catch(() => setNotFound(true));
  }, [slug]);

  const T = UI.pages.article;
  usePageMeta(a ? `${a.seoTitle || a.title} | KairosHK` : "Insights | KairosHK", a?.seoDescription || a?.standfirst);

  if (notFound)
    return (
      <div className="k-container k-section" data-testid="article-not-found">
        <h1 className="k-h2 mb-6">{T.notFound}</h1>
        <ArrowLink to="/insights" testId="back-to-insights-link">{T.back}</ArrowLink>
      </div>
    );
  if (!a) return <div className="k-container k-section text-ink-muted">{T.loading}</div>;

  const updated = a.updatedAt && a.updatedAt !== a.publishedAt;

  return (
    <article data-testid="article-page">
      <header className="bg-paper-alt border-b border-line">
        <div className="k-container py-16 max-w-[900px]">
          <Reveal>
            <nav className="text-xs text-ink-muted mb-8" aria-label="Breadcrumb" data-testid="article-breadcrumb">
              <Link to="/insights" className="text-brand no-underline">{T.insightsCrumb}</Link>
              <span className="mx-2">/</span>
              <Link to={`/insights/topics/${a.topic}`} className="text-brand no-underline">{a.topic.replace(/-/g, " ")}</Link>
            </nav>
            <p className="eyebrow mb-4">{T.publication}</p>
            <h1 className="k-h1 !text-[clamp(1.75rem,3.2vw,2.75rem)]" data-testid="article-title">{a.title}</h1>
            <p className="standfirst mt-5">{a.standfirst}</p>
            <div className="flex flex-wrap items-center gap-4 mt-8 text-sm text-ink-muted" style={{ fontVariantNumeric: "tabular-nums" }}>
              <span className="font-semibold text-ink">{a.author?.name}</span>
              <span>·</span>
              <span data-testid="article-date">{fmtDate(a.publishedAt)}</span>
              {updated && <span>· {T.updated} {fmtDate(a.updatedAt)}</span>}
              <span>·</span>
              <span>{a.readingTime} {UI.common.minRead}</span>
            </div>
          </Reveal>
        </div>
      </header>

      <div className="k-container max-w-[900px] py-14">
        <Reveal>
          <div className="prose-k" data-testid="article-body" dangerouslySetInnerHTML={{ __html: marked.parse(a.body || "") }} />
        </Reveal>

        {a.serviceLink && (
          <Reveal className="mt-16">
            <div className="border border-line p-8 bg-paper-alt" data-testid="article-service-cta">
              <p className="eyebrow mb-4">{T.relatedService}</p>
              <p className="text-ink mb-4">
                {T.relatedPre} <strong>{a.serviceLink.label}</strong>.
              </p>
              <ArrowLink to={a.serviceLink.href} testId="article-service-link">{a.serviceLink.label}</ArrowLink>
            </div>
          </Reveal>
        )}

        <p className="text-xs text-ink-muted leading-relaxed mt-12 border-t border-line pt-5" data-testid="article-disclaimer">{ARTICLE_DISCLAIMER}</p>

        <div className="mt-16 border-t border-line pt-10">
          <p className="eyebrow mb-4">{T.subscribe}</p>
          <SubscribeForm />
        </div>

        {a.related?.length > 0 && (
          <div className="mt-20">
            <p className="eyebrow mb-2">{T.moreOnTopic}</p>
            <div className="border-b border-line">
              {a.related.map((r) => <ArticleCard key={r.slug} a={r} />)}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
